import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import type { LegacyCaseDetail } from "../../../types/legacyCases";
import { useExtractLegacyCase } from "../../../hooks/useLegacyCases";
import {
  applyLegacyReportText,
  legacyDraftReportText,
  type LegacyReportText,
  type LegacyReviewForm,
} from "../../../utils/legacyReviewForm";
import LegacySourceEvidence from "./LegacySourceEvidence";
import LegacySourceDiscrepancies from "./LegacySourceDiscrepancies";
import LegacyExtractionComparison from "./LegacyExtractionComparison";
import LegacyExtractionWarnings from "./LegacyExtractionWarnings";
import { selectedLegacySourceComparison } from "../../../utils/legacySourceComparison";
import { legacyPatientSourceChange } from "../../../utils/legacyPatientMapping";

interface Props {
  form: LegacyReviewForm;
  detail: LegacyCaseDetail;
}

export default function LegacySourceControls({ form, detail }: Props) {
  const extract = useExtractLegacyCase(detail.id);
  const [replaceOpened, setReplaceOpened] = useState(false);
  const [notice, setNotice] = useState("");
  const [previousText, setPreviousText] = useState<LegacyReportText>();
  const reports = useMemo(
    () =>
      detail.documents
        .filter((document) => document.kind === "report")
        .map((document) => ({
          value: document.id,
          label: document.originalFileName,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [detail.documents]
  );
  const rows = useMemo(
    () =>
      detail.sourceRows.map((row) => ({
        value: row.id,
        label: `${row.sourceFileName} · ${row.sheetName} · row ${row.rowNumber}`,
      })),
    [detail.sourceRows]
  );
  const extractions = useMemo(
    () =>
      detail.extractions.filter(
        (entry) => entry.documentId === form.values.reportDocumentId
      ),
    [detail.extractions, form.values.reportDocumentId]
  );
  const options = useMemo(
    () =>
      extractions.map((entry) => ({
        value: entry.id,
        label: `${new Date(entry.createdAt).toLocaleString()} · ${
          entry.result.parserVersion
        }`,
      })),
    [extractions]
  );
  const extraction = useMemo(
    () => extractions.find((entry) => entry.id === form.values.extractionId),
    [extractions, form.values.extractionId]
  );
  const comparison = useMemo(
    () => selectedLegacySourceComparison(detail, form.values),
    [detail, form.values]
  );
  const olderExtraction = useMemo(
    () =>
      Boolean(
        extraction &&
          detail.currentParserVersion &&
          extraction.result.parserVersion !== detail.currentParserVersion
      ),
    [detail.currentParserVersion, extraction]
  );
  const handleSourceRow = useCallback(
    (value: string | null) => {
      if (form.getValues().sourceRowId === (value || "")) return;
      form.setValues(
        legacyPatientSourceChange(form.getValues().sourceRowId, value || "")
      );
      form.setFieldValue("sourceRowId", value || "");
      form.setFieldValue("conflictsAcknowledged", false);
    },
    [form]
  );
  const handleExtraction = useCallback(
    (value: string | null) => {
      if (form.getValues().extractionId === (value || "")) return;
      form.setValues(
        legacyPatientSourceChange(form.getValues().extractionId, value || "")
      );
      form.setFieldValue("extractionId", value || "");
      form.setFieldValue("conflictsAcknowledged", false);
      setNotice(
        "The selected extraction changed. Compare it with your retained draft before applying or importing."
      );
    },
    [form]
  );
  const handleReport = useCallback(
    (value: string | null) => {
      if (form.getValues().reportDocumentId === (value || "")) return;
      form.setValues(
        legacyPatientSourceChange(
          form.getValues().reportDocumentId,
          value || ""
        )
      );
      form.setFieldValue("reportDocumentId", value || "");
      form.setFieldValue("extractionId", "");
      form.setFieldValue("conflictsAcknowledged", false);
      setNotice(
        "The selected report changed. Review the existing draft text against this report before importing."
      );
    },
    [form]
  );
  const handleExtract = useCallback(async () => {
    const reportDocumentId = form.getValues().reportDocumentId;
    if (!reportDocumentId) {
      form.setFieldError("reportDocumentId", "Select the final report first.");
      return;
    }
    try {
      const updated = await extract.mutateAsync(reportDocumentId);
      if (form.getValues().reportDocumentId !== reportDocumentId) return;
      const latest = updated.extractions.find(
        (entry) => entry.documentId === reportDocumentId
      );
      if (latest) {
        form.setValues(
          legacyPatientSourceChange(form.getValues().extractionId, latest.id)
        );
        form.setFieldValue("extractionId", latest.id);
        form.setFieldValue("conflictsAcknowledged", false);
      }
      setNotice(
        "Extraction is ready. Your draft text is unchanged; use the extracted report text when you are ready to review it."
      );
    } catch {
      /* The translated mutation error stays visible below. */
    }
  }, [extract, form]);
  const handleOpenReplace = useCallback(() => setReplaceOpened(true), []);
  const handleCloseReplace = useCallback(() => setReplaceOpened(false), []);
  const handleReplace = useCallback(() => {
    if (extraction) {
      setPreviousText(legacyDraftReportText(form.getValues()));
      form.setValues(applyLegacyReportText(form.getValues(), extraction));
    }
    setReplaceOpened(false);
    setNotice(
      "Report text copied into the draft. Review the fields and save your changes."
    );
  }, [extraction, form]);
  const handleUndoReplace = useCallback(() => {
    if (previousText)
      form.setValues({ ...previousText, conflictsAcknowledged: false });
    setPreviousText(undefined);
    setNotice(
      "Your previous draft text is restored. Review the selected source before saving."
    );
  }, [form, previousText]);

  return (
    <Stack gap="md">
      <Title order={3}>Source and final report</Title>
      <Select
        label="Source spreadsheet row"
        withAsterisk
        aria-required
        data={rows}
        allowDeselect={false}
        clearable={false}
        {...form.getInputProps("sourceRowId")}
        onChange={handleSourceRow}
      />
      {olderExtraction && (
        <Alert
          color="orange"
          title="Updated extraction available"
          role="status"
        >
          This draft uses an older extraction. Extract the report again, compare
          the results, and use the extracted text when ready. Your edited fields
          and saved review stay unchanged until you apply and save the new text.
        </Alert>
      )}
      <Select
        label="Authoritative final report"
        withAsterisk
        aria-required
        data={reports}
        allowDeselect={false}
        clearable={false}
        {...form.getInputProps("reportDocumentId")}
        onChange={handleReport}
      />
      <Group>
        <Button
          variant="light"
          onClick={handleExtract}
          loading={extract.isPending}
        >
          Extract report text
        </Button>
      </Group>
      {extract.error && (
        <Alert color="red" title="Extraction failed">
          {extract.error.message}
        </Alert>
      )}
      {notice && (
        <Alert color="blue" role="status">
          {notice}
        </Alert>
      )}
      {extractions.length > 0 && (
        <Stack gap="sm">
          <Select
            label="Report extraction"
            data={options}
            allowDeselect={false}
            clearable={false}
            {...form.getInputProps("extractionId")}
            onChange={handleExtraction}
          />
          <Button
            variant="default"
            onClick={handleOpenReplace}
            disabled={!extraction}
          >
            Use extracted report text
          </Button>
          {previousText && (
            <Button variant="subtle" onClick={handleUndoReplace}>
              Undo report text replacement
            </Button>
          )}
        </Stack>
      )}
      <LegacySourceDiscrepancies
        comparison={comparison}
        hasRowConflicts={detail.hasConflicts}
      />
      <LegacyExtractionWarnings warnings={extraction?.result.warnings ?? []} />
      <LegacySourceEvidence
        detail={detail}
        sourceRowId={form.values.sourceRowId}
        extraction={extraction}
      />
      <Checkbox
        label="I reviewed source differences and used the final report for the historical report state"
        {...form.getInputProps("conflictsAcknowledged", { type: "checkbox" })}
      />
      <Modal
        opened={replaceOpened}
        onClose={handleCloseReplace}
        title="Replace draft report text?"
        centered
        size="xl"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <Text>
            This replaces the draft narrative fields with the selected
            extraction. Patient selections, dates, specimens, and previously
            saved revisions are retained.
          </Text>
          <LegacyExtractionComparison
            values={form.values}
            extraction={extraction}
          />
          <Group justify="space-between">
            <Button
              variant="default"
              onClick={handleCloseReplace}
              data-autofocus
            >
              Keep draft text
            </Button>
            <Button onClick={handleReplace}>Use extracted text</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
